import { previewStaticPaths } from 'scripts/preview-routes.cjs'
/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { filterProducts } from 'src/shared/frontendTypes/productsFilter'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

/* Functions */
import { post } from 'src/functions/fetch'
import { loadTemplateListingRoutes } from 'src/functions/templateListingRoutes.server'
import { loadListingPageData } from 'src/functions/pageData'
import { saveUrl } from 'src/functions/regex'
import { listingPageNumber, listingPagePath, withBrandContext } from 'src/functions/listingSeo'
import { mergeBrandMenuRoutes } from 'src/functions/templateListingRoutes'

/* Options */
import config from 'src/config'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/index'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const withoutUndefined = <T extends Record<string, unknown>>(values: T): Partial<T> =>
  Object.entries(values).reduce<Partial<T>>((result, [key, value]) => {
    if (value !== undefined) result[key as keyof T] = value as T[keyof T]
    return result
  }, {})


/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweitere die Props um die SEO-Felder */
interface Props {
  bid: number
  mid: number
  title?: string
  description?: string
  products: tProductPreview[]
  count: number
  productsGeneratedAt: number
  initialPage: number
  filterMetadata: Pick<filterProducts, 'filterTree' | 'filterBrands'>
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  stock?: boolean
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const FilterPage: React.FC<Props> = ({ bid, mid, title, products, count, productsGeneratedAt, initialPage, filterMetadata, metaTitle, metaDescription, description, metaKeywords, canonicalUrl, stock, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated }: Props) =>
  <Filter {...{ type: 'brandMenu', bid, mid, title, products, count, productsGeneratedAt, initialPage, filterMetadata, metaTitle, metaDescription, description, metaKeywords, canonicalUrl, stock, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated }} />

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getStaticPaths: GetStaticPaths = async () => {
  const preview = previewStaticPaths('brandMenu')
  if (preview) return preview

  const size = (() => {
    switch (process.env.CACHE) {
      case 'static': return config.cache_static_menues_size
      case 'ssr':    return config.cache_ssr_menues_size
      default:       return 1
    }
  })()

  if (size <= 0) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const data = await post({
    url: 'build/menuUrls/brandsMenues', 
    body: { 
      ...config.shop, 
      size,
      menu: process.env.NEXT_PUBLIC_MENU,
      token: process.env.BUILD_TOKEN,
    }, 
    local: true
  })

  type item = { 
    bid: number; b_path: string
    mid: number; m_path: string
  }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj, 'bid') && typeof obj.bid === 'number' &&
      hasOwnProperty(obj, 'b_path') && typeof obj.b_path === 'string' &&
      hasOwnProperty(obj, 'mid') && typeof obj.mid === 'number' &&
      hasOwnProperty(obj, 'm_path') && typeof obj.m_path === 'string'
    )

  if (!isItems(data)) {
    throw new Error('Brand/category build paths could not be loaded.')
  }

  const paths = mergeBrandMenuRoutes(data, loadTemplateListingRoutes()?.brandsMenues).map(({
    bid: p_bid, b_path,
    mid: p_mid, m_path,
  }: item) => {
    const bid = p_bid.toString()
    const mid = p_mid.toString()
    const b_alias = saveUrl(b_path || bid)
    const m_alias = saveUrl(m_path || mid)

    return {
      params: { bid, mid, b_alias, m_alias },
    }
  })

  return {
    paths: paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

/* ------------------------------------------------------------------------------- */

interface IParams extends ParsedUrlQuery {
  bid: string;
  mid: string;
  b_alias: string;
  m_alias: string;
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { bid, b_alias, mid, m_alias } = params as IParams
  const bidNumber = Number(bid)
  const midNumber = Number(mid)

  const initialPage = listingPageNumber(params?.page)
  if (!initialPage) return { notFound: true, revalidate: 60 }

  const { data, siteSettings: readSiteSettings } = await loadListingPageData({
    ...config.shop,
    page: initialPage,
    size: 24,
    brandId: bidNumber,
    menuId: midNumber,
    filterTree: true,
    filterBrands: true,
  })
  const siteSettings = await readSiteSettings()

  if (!data.brand || !data.menu || (initialPage > 1 && (initialPage - 1) * 24 >= data.filterProducts.count)) {
    return { notFound: true, revalidate: 60 }
  }

  const canonicalBrandAlias = saveUrl(data.brand?.path || b_alias || String(bidNumber))
  const canonicalMenuAlias = saveUrl(data.menu?.path || m_alias || String(midNumber))
  const canonicalUrl = listingPagePath(`b/${canonicalBrandAlias}/${bidNumber}/m/${canonicalMenuAlias}/${midNumber}`, initialPage)
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings.landingPageImageAiGenerated,
  )

  if (
    (params?.page !== undefined && initialPage === 1)
    || (canonicalBrandAlias && canonicalBrandAlias !== b_alias)
    || (canonicalMenuAlias && canonicalMenuAlias !== m_alias)
  ) {
    return {
      redirect: {
        destination: `/${canonicalUrl}`,
        permanent: true,
      },
    }
  }

  return {
    props: {
      bid: bidNumber,
      mid: midNumber,
      ...withoutUndefined({
        title: withBrandContext(data.menu?.titel, data.brand?.titel),
        metaTitle: data.menu?.metaTitle?.trim() || withBrandContext(data.menu?.titel, data.brand?.titel),
        metaDescription: data.menu?.metaDescription?.trim() || `${withBrandContext(data.menu?.titel, data.brand?.titel)} online entdecken. Passende Produkte und Varianten im Sortiment vergleichen.`,
        metaKeywords: data.menu?.metaKeywords,
        description: data.menu?.description,
        stock: data.menu?.stock,
      }),
      ...(data.menu?.pageBuilder ? { pageBuilder: data.menu.pageBuilder } : {}),
      ...(data.menu?.magazines ? { magazines: data.menu.magazines } : {}),
      count: data?.filterProducts.count || 0,
      products: data?.filterProducts.products || [],
      productsGeneratedAt: Date.now(),
      initialPage,
      filterMetadata: { filterTree: data.filterProducts.filterTree || [], filterBrands: data.filterProducts.filterBrands || [] },
      canonicalUrl,
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(landingPageImageAiGenerated ? { landingPageImageAiGenerated } : {}),
    },
    revalidate: 60,
  }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
